import { useEffect, useState, useRef, useMemo, useCallback } from "react"
import styles from "./Feed.module.scss"
import { connect } from "react-redux"
import {
  openFeedTextModal,
  closeMainModal,
  getPosteos,
  getOwnPosteos,
  resetRecentPost,
  getAllPortfolios,
} from "../../../store/actions"
import usePremiumCheck from "../../../hooks/usePremiumCheck"
import FeedCard from "./FeedCard"
import PortfolioCard from "./PortfolioCard/PortfolioCard"
import { SlidersHorizontal, FileText, ChevronDown } from "lucide-react"
import FeedCardSkeleton from "./FeedCardSkeleton"
import InfiniteScroll from "react-infinite-scroller"
import useUserImage from "../../../hooks/useUserImage"
import useIsPhone from "../../../hooks/useIsPhone"


const Feed = ({
    userData,
    openFeedTextModal,
    isUserPremium,
    closeMainModal,
    getPosteos,
    getOwnPosteos,
    ownPosteos,
    resetRecentPost,
    posteos,
    recentPost,
    sideNavBarExpanded,
    componentLoading,
    getAllPortfolios,
    allPortfolios,
    ...props
  }) => {
    const [selectedOption, setSelectedOption] = useState("Mostrar")
    const [myPosts, setMyPosts] = useState(false)
    const [shuffleKey, setShuffleKey] = useState(0)
    const [showFeedBar, setShowFeedBar] = useState(false)
    const [lastScrollPosition, setLastScrollPosition] = useState(0)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
  
    const isPhone = useIsPhone()
    const buttonsContainerRef = useRef(null)
    const observerRef = useRef(null)
    const loadMoreTimeoutRef = useRef(null)
  

    const mixPostsAndPortfolios = useCallback((posts, portfolios) => {
      const postsWithType = (posts || []).map((post) => ({ ...post, itemType: "post" }))
      const portfoliosWithType = (portfolios || []).map((portfolio) => ({ ...portfolio, itemType: "portfolio" }))
  
      const allItems = [...postsWithType, ...portfoliosWithType]
      return allItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    }, [])
  
    const shuffleArray = useCallback((array) => {
      const shuffled = [...array]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }
      return shuffled
    }, [])
  
    const items = useMemo(() => {
      if (!dataLoaded) return []
  
      let baseItems = []
  
      switch (selectedOption) {
        case "propios":
          const userPortfolios = (allPortfolios || []).filter((portfolio) => portfolio.user?.id === userData.id)
          baseItems = mixPostsAndPortfolios(ownPosteos || [], userPortfolios)
          break
        case "productos":
          const productos = (allPortfolios || []).filter((portfolio) => portfolio.type === "producto")
          baseItems = productos.map((portfolio) => ({ ...portfolio, itemType: "portfolio" }))
          baseItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break
        case "servicios":
          const servicios = (allPortfolios || []).filter((portfolio) => portfolio.type === "servicio")
          baseItems = servicios.map((portfolio) => ({ ...portfolio, itemType: "portfolio" }))
          baseItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break
        case "posteos":
          baseItems = posteos ? posteos.map((post) => ({ ...post, itemType: "post" })) : []
          baseItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break
        case "recomendados":
          baseItems = mixPostsAndPortfolios(posteos || [], allPortfolios || [])
          break
        case "recientes":
        case "Mostrar":
        default:
          baseItems = mixPostsAndPortfolios(posteos || [], allPortfolios || [])
          break
      }
  
      if (selectedOption === "Mostrar" && shuffleKey > 0) {
        return shuffleArray(baseItems)
      }
  
      return baseItems
    }, [
      selectedOption,
      posteos,
      ownPosteos,
      allPortfolios,
      userData.id,
      mixPostsAndPortfolios,
      shuffleArray,
      shuffleKey,
      dataLoaded,
    ])
  
    useEffect(() => {
      const options = {
        root: null,
        rootMargin: isPhone ? "-50px 0px 0px 0px" : "0px",
        threshold: [0, 0.9],
      }
  
      observerRef.current = new IntersectionObserver((entries) => {
        const [entry] = entries
        const currentScroll = window.scrollY
        const direction = currentScroll > lastScrollPosition ? "down" : "up"
        setLastScrollPosition(currentScroll)
  
        if (direction === "down") {
          setShowFeedBar(entry.intersectionRatio < 0.9)
        } else if (direction === "up") {
          setShowFeedBar(
            entry.intersectionRatio < 0.9 && currentScroll > (buttonsContainerRef.current?.offsetHeight || 0),
          )
        }
      }, options)
  
      if (buttonsContainerRef.current) {
        observerRef.current.observe(buttonsContainerRef.current)
      }
  
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect()
        }
      }
    }, [lastScrollPosition, isPhone])
  
    const { imageError, initials, profileImage } = useUserImage(userData)
    const premiumCheck = usePremiumCheck()
  
    useEffect(() => {
      const loadInitialData = async () => {
        try {
          if (!posteos || posteos.length === 0) {
            await getPosteos("DESC", "DATE")
          }
          if ((!allPortfolios || allPortfolios.length === 0) && userData?.id) {
            await getAllPortfolios(userData.id)
          }
          resetRecentPost()
          setDataLoaded(true)
        } catch (error) {
          console.error("Error loading initial data:", error)
          setDataLoaded(true)
        }
      }
  
      if (userData?.id && !dataLoaded) {
        loadInitialData()
      }
    }, [userData?.id, dataLoaded])
  
    useEffect(() => {
      if (!dataLoaded) return
  
      setMyPosts(false)
  
      const handleOptionChange = async () => {
        try {
          switch (selectedOption) {
            case "recomendados":
              await getPosteos("ASC", "MG")
              break
            case "recientes":
              await getPosteos("DESC", "DATE")
              break
            case "propios":
              await getOwnPosteos(userData.id)
              setMyPosts(true)
              break
            default:
                if (selectedOption === "Mostrar") {
                    setShuffleKey((prev) => prev + 1) 
                    }
                break
            }
        } catch (error) {
          console.error("Error changing option:", error)
        }
      }
  
      handleOptionChange()
    }, [selectedOption, dataLoaded, userData.id])
  
    useEffect(() => {
      if (recentPost?.user?.id === userData?.id) {
        getPosteos("DESC", "DATE")
        setSelectedOption("recientes")
      }
    }, [recentPost, userData?.id])

    useEffect(() => {
      return () => {
        if (loadMoreTimeoutRef.current) {
          clearTimeout(loadMoreTimeoutRef.current)
        }
      }
    }, [])

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (showDropdown && !event.target.closest(`.${styles.dropdown}`)) {
          setShowDropdown(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showDropdown])
  
    const handleOpenMainModalTxt = () => {
      openFeedTextModal({
        text: true,
      })
    }
  
    const handleOptionClick = (option) => {
      setSelectedOption(option)
      setShuffleKey(0)
      setShowDropdown(false)
    }
  
    const handleShuffle = useCallback(() => {
      if (myPosts || isLoadingMore || !["Mostrar", "recientes", "recomendados"].includes(selectedOption)) return
  
      setIsLoadingMore(true)
  
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current)
      }
  
      loadMoreTimeoutRef.current = setTimeout(() => {
        setShuffleKey((prev) => prev + 1)
        setIsLoadingMore(false)
      }, 1500)
    }, [myPosts, isLoadingMore, selectedOption])

    const generateStableKey = useCallback(
      (item, index) => {
        const baseKey = item.itemType === "portfolio" ? `portfolio-${item.id}` : `post-${item.id}`
        return `${baseKey}-${selectedOption}-${index}`
      },
      [selectedOption],
    )
  
    return (
          <div className={styles.container} style={{ gridArea: 'feed' }}>
          <div className={styles.header}>
          <h2 className={styles.header_title}>Productos y servicios</h2>
          <div className={styles.header_select}>
            <div className={styles.dropdown}>
              <span className={styles.header_line} />
              <SlidersHorizontal size={14} style={{ color: "#000", marginRight: "1rem", minWidth: "1.4rem" }} />
              <button
                className={styles.dropdown_button}
                onClick={() => {
                  setShowDropdown(!showDropdown)
                }}
              >
                {selectedOption === "Mostrar" ? "Mostrar" : 
                 selectedOption === "recientes" ? "Todos (Recientes)" :
                 selectedOption === "recomendados" ? "Todos (Recomendados)" :
                 selectedOption === "productos" ? "Solo Productos" :
                 selectedOption === "servicios" ? "Solo Servicios" :
                 selectedOption === "posteos" ? "Solo Posteos" :
                 selectedOption === "propios" ? "Mis Publicaciones" : "Mostrar"}
                <ChevronDown 
                  size={16} 
                  className={`${styles.chevron} ${showDropdown ? styles.chevronRotated : ''}`}
                />
              </button>
              
              {showDropdown && (
                <div className={styles.dropdown_content}>
                  <button
                    className={`${styles.dropdown_content_option} ${selectedOption === "recientes" ? styles.active : ""}`}
                    onClick={() => handleOptionClick("recientes")}
                  >
                    Todos (Recientes)
                  </button>
                  <button
                    className={`${styles.dropdown_content_option} ${selectedOption === "recomendados" ? styles.active : ""}`}
                    onClick={() => handleOptionClick("recomendados")}
                  >
                    Todos (Recomendados)
                  </button>
                  <button
                    className={`${styles.dropdown_content_option} ${selectedOption === "productos" ? styles.active : ""}`}
                    onClick={() => handleOptionClick("productos")}
                  >
                    Solo Productos
                  </button>
                  <button
                    className={`${styles.dropdown_content_option} ${selectedOption === "servicios" ? styles.active : ""}`}
                    onClick={() => handleOptionClick("servicios")}
                  >
                    Solo Servicios
                  </button>
                  <button
                    className={`${styles.dropdown_content_option} ${selectedOption === "posteos" ? styles.active : ""}`}
                    onClick={() => handleOptionClick("posteos")}
                  >
                    Solo Posteos
                  </button>
                  <button
                    className={`${styles.dropdown_content_option} ${selectedOption === "propios" ? styles.active : ""}`}
                    onClick={() => handleOptionClick("propios")}
                  >
                    Mis Publicaciones
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
  
        <div className={styles.feedItemContainer}>
          <InfiniteScroll
            pageStart={0}
            loadMore={handleShuffle}
            hasMore={
              ["Mostrar", "recientes", "recomendados"].includes(selectedOption) &&
              !myPosts &&
              items.length > 0 &&
              !isLoadingMore
            }
            threshold={300}
            className={styles.feedItemContainer}
          >
            {componentLoading && items?.length > 0 ? (
              Array.from({ length: 10 }).map((_, index) => <FeedCardSkeleton key={`skeleton-${index}`} />)
            ) : items?.length > 0 ? (
              items?.map((item, index) => {
                const stableKey = generateStableKey(item, index)
                if (item.itemType === "portfolio") {
                  return <PortfolioCard key={stableKey} item={item} />
                } else {
                  return <FeedCard key={stableKey} item={item} myPosts={myPosts} />
                }
              })
            ) : (
              <div className={styles.feedItemContainer_empty}>
                <div className={styles.feedItemContainer_empty_icon}>
                  <FileText size={34} />
                </div>
                <h2 className={styles.feedItemContainer_empty_title}>No tenés publicaciones propias</h2>
                <p className={styles.feedItemContainer_empty_text}>
                  Comparte novedades, fotos y actualizaciones con la comunidad
                </p>
                <button className={styles.feedItemContainer_empty_button} onClick={premiumCheck(handleOpenMainModalTxt)}>
                  Crear publicación
                </button>
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>
    )
  }
  
  const mapStateToProps = (state) => {
    return {
      userData: state.general.userData,
      posteos: state.dashboard.posteos,
      ownPosteos: state.dashboard.ownPosteos,
      recentPost: state.dashboard.recentPost,
      isUserPremium: state.general.isUserPremium,
      sideNavBarExpanded: state.general.sideNavBarExpanded,
      componentLoading: state.general.componentLoading,
      allPortfolios: state.dashboard.allPortfolios,
    }
  }
  
  export default connect(mapStateToProps, {
    openFeedTextModal,
    closeMainModal,
    getPosteos,
    getOwnPosteos,
    resetRecentPost,
    getAllPortfolios,
  })(Feed)